<template>
  <div
    :id="`${id}-container`"
    class="autocomplete-container py-1"
    @click.stop="onPanelClick"
  >
    <div class="selected-panel d-flex flex-wrap ml-3">
      <div
        v-for="url in selected"
        :key="url"
        class="selected-item px-2 py-1 mr-3 my-1"
      >
        <v-icon
          class="remove-btn"
          :size="21"
          @click.prevent.stop="removeItem(url)"
        >
          close
        </v-icon>
        <a :href="`https://${url}`" target="_blank">
          {{ url }}
        </a>
      </div>
    </div>
    <v-text-field
      :id="`${id}-text-field`"
      v-if="visibility"
      v-model="input"
      class="pa-0"
      dense
      flat
      solo
      hide-details
      clearable
      @keydown.esc.stop="onEscInput($event)"
      @keydown.tab.prevent.stop="onItemTab(0, $event)"
      @keydown.down.prevent.stop="focusItemIndex(0)"
      @blur="inputBlur"
      @focus="inputFocus($event)"
    />
    <div
      :id="id"
      v-if="id && items.length > 0 && panelVisible"
      class="autocomplete-panel"
      :style="`width: ${panelWidth}px;`"
    >
      <div
        tabindex="0"
        :id="`${id}-${item.index}`"
        class="autocomplete-item pl-3 py-1"
        v-for="item in items"
        :key="item.index"
        @click.stop="selectItem(item)"
        @keydown.esc.stop="changeFocusToInput"
        @keydown.enter.stop="selectItem(item)"
        @keydown.tab.prevent.stop="onItemTab(item.index + 1, $event)"
        @keydown.down.prevent.stop="focusItemIndex(item.index + 1)"
        @keydown.up.prevent.stop="focusItemIndex(item.index - 1)"
        @blur="itemBlur"
        @focus="itemFocus"
      >
        {{ item.url }}
      </div>
    </div>
  </div>
</template>
<script>
import { Debouncer } from '@/shared/services/debouncer';
import { v4 as uuid } from 'uuid';
import { UrlAutocompleter } from '../SearchUtils';

export default {
  name: 'UrlAutocompleter',
  props: {
    initialUrls: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    visibility: false,
    items: [],
    selected: [],
    input: '',
    urlAutocompleter: undefined,
    debouncer: undefined,
    id: undefined,
    panelVisible: true,
    panelWidth: 600,
    itemFocusState: false,
    inputFocusState: false,
  }),
  methods: {
    async updateItems() {
      const response = await this.urlAutocompleter.getCompletion(this.input);
      this.items = response.urls.map((url, index) => ({ url, index }));
    },
    async selectItem(item) {
      if (!this.selected.includes(item.url)) {
        this.selected.push(item.url);
      } else {
        this.removeItem(item.url);
      }
    },
    onPanelClick() {
      this.visibility = true;
      setTimeout(() => this.changeFocusToInput()); // Javascript -_-
    },
    focusItemIndex(index) {
      const lastElementIndex = this.items.length - 1;

      const elementIndex = () => {
        if (index < 0) {
          return lastElementIndex;
        }

        if (index > lastElementIndex) {
          return 0;
        }

        return index;
      };

      executeOnElement(`${this.id}-${elementIndex()}`, (element) => element.focus());
    },
    changeFocusToInput() {
      executeOnElement(`${this.id}-text-field`, (element) => element.focus());
    },
    inputFocus(event) {
      this.inputFocusState = true;
      this.updatePanelWidth();
      this.panelVisible = true;
      this.visibility = true;
      this.onSelectTextInput(event);
    },
    inputBlur() {
      this.inputFocusState = false;
      this.maybeHidePanel();
    },
    maybeHidePanel() {
      setTimeout(() => {
        if (!this.itemFocusState && !this.inputFocusState) {
          this.hidePanel();
        }
      }, 100);
    },
    hidePanel() {
      this.panelVisible = false;
      this.visibility = false;
    },
    itemFocus() {
      this.itemFocusState = true;
    },
    itemBlur() {
      this.itemFocusState = false;
      this.maybeHidePanel();
    },
    removeItem(url) {
      this.selected = this.selected.filter(selected => selected !== url);
    },
    updatePanelWidth() {
      executeOnElement(`${this.id}-container`, (element) => {
        this.panelWidth = element.getBoundingClientRect().width;
      });
    },
    onItemTab(index, e) {
      this.handleTabPress(
        () => this.focusItemIndex(index),
        () => this.focusItemIndex(index - 2),
        e,
      );
    },
    handleTabPress(onTabCallback, onShiftTabCallback, e) {
      if (e.shiftKey) {
        onShiftTabCallback();
      } else {
        onTabCallback();
      }
    },
    onSelectTextInput(event) {
      event.target.select();
    },
    onEscInput(event) {
      this.onSelectTextInput(event);
      this.hidePanel();
    },
  },
  created() {
    this.id = uuid();
    this.selected = [...this.initialUrls];
    this.urlAutocompleter = new UrlAutocompleter();
    this.debouncer = new Debouncer(() => this.updateItems(), 100);
  },
  mounted() {
    this.updatePanelWidth();
  },
  watch: {
    input() {
      this.debouncer.execute();
      this.panelVisible = true;
    },
    selected() {
      this.$emit('changed', this.selected);
    },
    panelVisible() {
      this.updatePanelWidth();
    },
  },
};

const executeOnElement = (id, callback) => {
  const element = document.getElementById(id);
  if (element) {
    callback(element);
  }
};

</script>
<style scoped>
.autocomplete-container {
  width: 100%;
  min-height: 48px;
  border: 1px solid #9e9e9e;
  border-radius: 4px;
  transition: outline-width 150ms linear;
}
.autocomplete-container:hover {
  border: 1px solid black;
}
.autocomplete-container:focus-within {
  outline: 2px solid #364bc8;
  border: 1px solid #364bc8;
}
.autocomplete-panel {
  z-index: 2;
  position: absolute;
  overflow: none;

  margin-left: -1px;
  border-top: 1px solid lightgrey;
  border-bottom: 1px solid #9e9e9e;
  border-left: 1px solid #9e9e9e;
  border-right: 1px solid #9e9e9e;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;

  background-color: white;
  text-align: left;
}
.autocomplete-item:focus {
  background-color: #dcdcdc;
  outline: none;
}
.autocomplete-item:hover {
  cursor: pointer;
  background-color: #dcdcdc;
}
.autocomplete-item:focus-visible {
  outline: none;
}
.autocomplete-panel:focus {
  outline: none;
}
.autocomplete-panel:focus-visible {
  outline: none;
}
.selected-panel {
  text-align: left;
  font-weight: 500;
  font-size: 12px;
}
.selected-item {
  border-radius: 12px;
  background-color: lightgrey;
  text-align: left;
  transition: all .2s ease-in-out;
}
.selected-item:hover {
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.50);
  z-index: 2;
  transform: scale(1.03);
  background-color: #E2E2E2;
}
.selected-item > a {
  color: rgba(0, 0, 0, 0.9);
  text-decoration: unset;
}
.selected-item > a:hover {
  text-decoration: underline;
}
.remove-btn {
  transition: all .1s ease-in-out;
}
.remove-btn:hover {
  color: red;
  font-weight: 600;
}
</style>
